<template>
    <div
        ref="editor"
        v-html="value"
        class="quill-editor"
        v-bind:class="theme"
    >
    </div>
</template>

<script>
    //https://pineco.de/wrapping-quill-editor-in-a-vue-component/
    
    import Quill from 'quill'
    import $ from 'jquery'
    
    import _ from 'lodash'
    import DnxVariableBlot from './CustomBlots/dnx-variable-blot'
    import dnxVariableOptions from './CustomBlots/dnx-variable-options';

    //Quill does not provide DOM events out of the box
    //We'll have to attach them to the root node ourselves
    const proxiedEvents = ['blur', 'focus'];
    
    const eventSources = {
        user: 'user',
        system: 'api'  
    };
    
    const themes = {
        none: 'none',
        snow: 'snow',
        bubble: 'bubble'
    };
    
    const formats = {
        background: 'background',
        bold: 'bold',
        color: 'color',
        font: 'font',
        code: 'code',
        italic: 'italic',
        link: 'link',
        size: 'size',
        strikethrough: 'strike',
        script: 'script',
        underline: 'underline',
        
        blockquote: 'blockquote',
        header: 'header',
        indent: 'indent',
        list: 'list',
        
        textAlign: 'align',
        textDirection: 'direction',
        codeBlock: 'code-block',
        
        image: 'image',
        video: 'video',
        dnxVariable: 'dnxVariable'
    };
    
    const toolbarInlineOpts = [{
        module: 'attributors/style/size',
        toolbarKey: 'size',
        
        //We can't apply a default size when a header is configured; 
        //size takes priority over our header
        canApplyDefault: (format) => !format.hasOwnProperty('header')
    }, {
        module: 'attributors/style/font',
        toolbarKey: 'font',
        canApplyDefault: () => true
    }, {
        module: 'attributors/style/align',
        toolbarKey: 'align',
        canApplyDefault: () => true
    }];
    export {
    
        themes,
        formats
    }
    
    export default {
        name: "html-editor",
        mounted() {
            this.initialize(this.value);
        },
        methods: {
            update(){
                const vm = this;
                
                vm.$emit('input', vm.editorValue)
            },

            //For external use
            setText(text){
                const vm = this;
                
                //A delta is required if we want to actually use our styles
                const delta = [{insert: text, attributes: vm.editor.getFormat() }];
                
                //If we don't set our contents in the next tick
                //we risk doing nothing
                vm.$nextTick(() => {
                    vm.editor.setContents(delta);
                });
            },
            initialize(value) {
                const vm = this;

                //Take all non-blacklisted formats
                const whitelistedFormats = vm.formats.filter(f => vm.formatsBlacklist.indexOf(f) === -1);
                const modules = {};

                if(vm.syntaxModule) modules.syntax = vm.syntaxModule;
                if(vm.clipboardModule) modules.clipboard = vm.clipboardModule;
                if(vm.historyModule) modules.history = vm.historyModule;
                if(vm.keyboardModule) modules.keyboard = vm.keyboardModule;

                if(vm.toolbarModule) {
                    //Register our Dnx variable blot.
                    vm.registerDnxVariable();

                    //Create a copy of our toolbar
                    //configureToolbar alters certain properties which
                    //need to be reset upon re-initialize
                    modules.toolbar = {};
                    modules.toolbar.container = _.cloneDeep(vm.toolbarModule);
                    configureToolbar(vm, modules.toolbar);
                }

                //Initialize our editor
                vm.editor = new Quill(vm.$refs.editor, {
                    modules: modules,
                    placeholder: vm.placeholder,
                    theme: vm.theme,
                    readOnly: vm.readOnly,
                    formats: whitelistedFormats,
                    bounds: document.body
                });

                //Add our html for the dnxVariable blot
                vm.insertDnxVariableHtml();

                if(!vm.isHtml(value)){
                    //Force our text to be properly formatted
                    vm.setText(value);
                }

                //Attach our event handlers
                proxiedEvents.forEach((evt) => vm.editor.root.addEventListener(evt, () => vm.$emit(evt, ...arguments)));

                vm.addEventListeners();

                //Our editor forces focus upon creation
                vm.$emit('focus');
            },
            addEventListeners() {
                const vm = this;

                const el = $(vm.$el);
                const quillTooltip = el.find('.ql-tooltip');
                
                //https://github.com/quilljs/quill/issues/1290
                quillTooltip.find('.ql-toolbar').on('mousedown', e => e.preventDefault());

                //Using the dynamic class bindings provided by Vue causes Vue to remove any classes added by Quill
                //Therefor we'll simply attach our own event handlers
                vm.editor.root.addEventListener('focus', () => el.addClass('focused') );

                vm.editor.root.addEventListener('blur', () => {
                    el.removeClass('focused');
                    
                    //Due to use blocking Quill's default mousedown event
                    //Quill will, at times, not add the 'ql-hidden' class to re-hide its tooltip
                    quillTooltip.addClass('ql-hidden');
                });

                const toolbarButtons = Array.prototype.slice.call(
                    document.querySelectorAll('.ql-toolbar button, .ql-toolbar .ql-picker-item')
                );

                //We want to make sure that adding styling to dnx variables will apply to the entire node.
                //So we'll check on toolbar buttons mouseup event, if it concerns a dnxVariable.
                //If it does and only part of the variable is selected, select the whole node before applying styling.
                toolbarButtons.forEach((button) => { 
                    button.addEventListener('mouseup', () => {
                        const selection = vm.editor.getSelection();
                        const index = selection != null ? selection.index : null;

                        if(index == null) return;
                            
                        const node = vm.editor.getLeaf(index)[0].domNode;
                        const parent = node.parentElement;
                        
                        if(parent.localName !== 'x-dnxvariable') return;
                        
                        if(selection.length < node.length) {
                            const offset = Quill.find(node).offset(vm.editor.scroll);
                            vm.editor.setSelection(offset, node.length);
                        }
                    }) 
                });
            },
            registerDnxVariable() {
                //Add custom blotName and our desired tag name.
                DnxVariableBlot.blotName = 'dnxVariable';
                DnxVariableBlot.tagName = 'X-DNXVARIABLE';
                
                Quill.debug('error'); //Suppress overwrite warning for custom module
                Quill.register('modules/dnxVariable', DnxVariableBlot);
                Quill.debug('warning'); //restore log level

                //Add module to inlineOpts for import.
                toolbarInlineOpts.push({
                    module: 'modules/dnxVariable',
                    toolbarKey: 'dnxVariable',
                    canApplyDefault: () => false
                });
            },
            insertDnxVariableHtml() {
                //Unfortunately we need to manually supply the html for our custom variables blot.
                const classSelector = '.ql-dnxVariable ';
                const placeholderPickerItems = Array.prototype.slice.call(document.querySelectorAll(classSelector + '.ql-picker-item'));
                
                placeholderPickerItems.forEach(item => item.textContent = item.dataset.value);
                const target = document.querySelector(classSelector + '.ql-picker-label');
                
                if(target !== null) {
                    target.innerHTML = 'Dnx variable';
                }
            },
            reInitialize(){
                const vm = this;
                
                //Quill 'default' values are set in stone
                //We'll need to remove, and re-initialize the editor for new defaults to work
                const value = vm.editorValue;
                
                vm.$refs.editor.innerHTML = null;
                
                vm.initialize(value);
            },
            isHtml(value){
                //Naive HTML check
                return !!value &&
                    value.startsWith('<') &&
                    value.endsWith('>');
            }
        },
        data(){
            return {
                editor: null
            }
        },
        computed: {
            editorValue(){
                return this.editor.getText() ? this.editor.root.innerHTML : '';
            }
        },
        watch: {
            editor(editor) {
                //Attach our event handlers etc...
                const vm = this;
                
                //Load our provided HTML
                editor.root.innerHTML = vm.value;
                
                editor.on('text-change', () => {
                    //Wait for the next tick to ensure our styles get applied to correctly
                    vm.$nextTick(() => {
                        vm.update();
                    })
                })
            }
        },
        props: {
            value: {
                type: String,
                default: ''
            },
            placeholder: {
                type: String,
                default: ''
            },
            readOnly: {
                type: Boolean,
                default: false
            },
            syntaxModule: {
                //https://quilljs.com/docs/modules/syntax/
                type: Boolean
            },
            clipboardModule: {
                //https://quilljs.com/docs/modules/clipboard/
                type: Object
            },
            historyModule: {
                //https://quilljs.com/docs/modules/history/
                type: Object
            },
            keyboardModule: {
                //https://quilljs.com/docs/modules/keyboard/
                type: Object
            },
            toolbarModule: {
                //https://quilljs.com/docs/modules/toolbar/
                type: [Object,Array]
            },
            toolbarDefaults: {
                type: Object,
                default: () => {},
                validator: (value) => {
                    const allowedValues = toolbarInlineOpts.map(x => x.toolbarKey);
                    const keys = Object.keys(value);
                    
                    return isSubsetOf(keys, allowedValues)
                }
            },
            formatsBlacklist: {
                type: Array,
                default: () => [],
                validator: (value) => {
                    const allowedValues = Object.values(formats);

                    //Verify whether one of the given elements is not present within our allowedValues
                    return isSubsetOf(value, allowedValues);
                }
            },
            formats: {
                type: Array,
                default: () => Object.values(formats),
                validator: (value) => {
                    const allowedValues = Object.values(formats);

                    //Verify whether one of the given elements is not present within our allowedValues
                    return isSubsetOf(value, allowedValues);
                }
            },
            theme: {
                type: String,
                default: themes.none,
                validator: (value) => {
                    return themes[value] !== undefined;
                }
            }
        }
    }
    
    function findToolbarGroup(toolbar, key){
        return flattenToolbar(toolbar).find(tg => tg.hasOwnProperty(key));
    }
    
    function flattenToolbar(toolbar){
        if(!toolbar){
            return [];
        }
        
        return toolbar.reduce((acc, cur) => {
            if(!Array.isArray(cur)){
                acc.push(cur);
            }
            else{
                //Push each individual item of the array
                Array.prototype.push.call(acc, ...cur);
            }

            return acc;
        }, []);
    }
    
    function configureToolbar(vm, toolbar){
        Object.keys(vm.toolbarDefaults).forEach(key => {
            const toolbarGroup = findToolbarGroup(toolbar.container, key);
            
            if(!toolbarGroup || !toolbarGroup[key]){
                return;
            }

            //Copy our option array as to not affect the parent component
            const option = toolbarGroup[key];

            //Remove our value
            option.splice(option.indexOf(vm.toolbarDefaults[key]), 1);
            
            //And move it to the front
            option.unshift(vm.toolbarDefaults[key]);
        });
        
        toolbarInlineOpts.forEach(inlineOpts => {
            //Import the quill module
            const quillModule = Quill.import(inlineOpts.module);

            //Find the toolbarGroup belonging to our whitelist key
            const toolbarGroup = findToolbarGroup(toolbar.container, inlineOpts.toolbarKey);

            //Skip values not present within both the toolbar/quill
            if(!quillModule || !toolbarGroup){
                return;
            }
 
            //Assume our toolbarGroup value to be an array for now
            const newWhitelist = toolbarGroup[inlineOpts.toolbarKey];

            //Skip empty arrays/falsy values
            if(!!newWhitelist && newWhitelist.length){
                quillModule.whitelist = toolbarGroup[inlineOpts.toolbarKey];
            }

            //Re-register the module
            Quill.register(quillModule, true);
        });
    }
    
    function isSubsetOf(set, other) {
        if(!other){
            return false;
        }
        
        if(set.length === 0){
            return true;
        }
        
        return set.filter(f => other.indexOf(f) === -1).length === 0;
    }
</script>

<style lang="stylus">
    @import "~quill/assets/core.styl"
    @import "~quill/assets/snow.styl"
    @import "~quill/assets/bubble.styl"
</style>


<style src="../Styles/html-editor.scss" lang="scss"></style>
